import { fetchApi } from './api';

/**
 * Service to validate user status after SSO/B2C authentication
 */
export class UserValidationService {
  
  /**
   * Check if user is active in UserMaster
   * @param {string} userEmail - User's email address
   * @returns {Promise<{isActive: boolean, user: object|null, error: string|null}>}
   */
  static async validateUserStatus(userEmail) {
    try {
      console.log('[UserValidation] Checking user status for:', userEmail);
      
      // Call UserMaster API to get user details
      const response = await fetchApi('/api/UserMaster/GetUserDetailsByEmail', {
        method: 'POST',
        body: JSON.stringify({ EmailID: userEmail }),
        skipLoader: true, // Don't show loader for this validation
        skipSlowlorisProtection: true // Bypass Slowloris protection for auth calls
      });
      
      console.log('[UserValidation] API Response:', JSON.stringify(response, null, 2));
      
      // Handle different response structures
      let userData = null;
      if (response?.data) {
        userData = response.data;
      } else if (Array.isArray(response) && response.length > 0) {
        userData = response[0];
      } else if (response && typeof response === 'object') {
        userData = response;
      }
      
      if (!userData) {
        return {
          isActive: false,
          user: null,
          error: 'User not found in system'
        };
      }
      
      // Check if user status is Active
      const isActive = userData.Status === 'Active' || userData.status === 'Active';
      
      return {
        isActive,
        user: userData,
        error: isActive ? null : 'User account is inactive'
      };
      
    } catch (error) {
      console.error('[UserValidation] Error validating user status:', error);
      return {
        isActive: false,
        user: null,
        error: `Validation failed: ${error.message}`
      };
    }
  }
  
  /**
   * Logout user and clear all session data
   * @param {object} msalInstance - MSAL instance for SSO logout
   * @param {function} navigate - React Router navigate function
   * @param {string} reason - Reason for logout
   */
  static async logoutUser(msalInstance, navigate, reason = 'User validation failed') {
    try {
      console.log('[UserValidation] Logging out user:', reason);
      
      // Clear all local storage and session storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Check if it's a B2C user (supplier)
      const isSupplier = sessionStorage.getItem('user_type') === 'supplier';
      
      if (isSupplier) {
        // For B2C users, just clear storage and redirect
        navigate('/login', { replace: true });
      } else {
        // For SSO users, use MSAL logout
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          await msalInstance.logoutRedirect({
            account: accounts[0],
            postLogoutRedirectUri: window.location.origin + '/login'
          });
        } else {
          navigate('/login', { replace: true });
        }
      }
      
    } catch (error) {
      console.error('[UserValidation] Error during logout:', error);
      // Fallback: clear storage and redirect
      localStorage.clear();
      sessionStorage.clear();
      navigate('/login', { replace: true });
    }
  }
  
  /**
   * Show error message to user
   * @param {string} message - Error message to display
   */
  static showErrorMessage(message) {
    // Create and show error modal/alert
    const errorDiv = document.createElement('div');
    errorDiv.className = 'user-validation-error-overlay';
    errorDiv.innerHTML = `
      <div class="user-validation-error-modal">
        <div class="error-header">
          <h3>Access Denied</h3>
        </div>
        <div class="error-body">
          <p>${message}</p>
          <p>Please contact your administrator for assistance.</p>
        </div>
        <div class="error-footer">
          <button onclick="this.closest('.user-validation-error-overlay').remove()" class="error-ok-btn">
            OK
          </button>
        </div>
      </div>
    `;
    
    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      .user-validation-error-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(74, 74, 74, 0.8);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 99999;
      }
      .user-validation-error-modal {
        background: #ffffff;
        border-radius: 10px;
        padding: 0;
        max-width: 420px;
        width: 90%;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        border: 1px solid #F1F1ED;
      }
      .error-header {
        background: #810055;
        color: #ffffff;
        padding: 16px 24px;
        border-radius: 10px 10px 0 0;
      }
      .error-header h3 {
        margin: 0;
        font-size: 1.125rem;
        font-family: 'GB-Headline', sans-serif;
      }
      .error-body {
        padding: 24px;
        text-align: center;
      }
      .error-body p {
        margin: 10px 0;
        color: #333333;
        font-family: 'GB-BodyCopy', sans-serif;
        font-size: 0.875rem;
        line-height: 1.5;
      }
      .error-footer {
        padding: 16px 24px;
        text-align: center;
        border-top: 1px solid #F1F1ED;
      }
      .error-ok-btn {
        background: #810055;
        color: #ffffff;
        border: none;
        padding: 10px 40px;
        border-radius: 50rem;
        cursor: pointer;
        font-size: 1rem;
        font-family: 'GB-BodyCopyHighlight', sans-serif;
        transition: background-color 0.3s ease;
      }
      .error-ok-btn:hover {
        background: #5c003d;
      }
    `;
    
    document.head.appendChild(style);
    document.body.appendChild(errorDiv);
    
    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.remove();
      }
      if (style.parentNode) {
        style.remove();
      }
    }, 10000);
  }
}

export default UserValidationService;